import type { TimeZone } from '@shared/utils/date';
import { createContext, type ReactNode, useContext, useState } from 'react';
import { getActivePreset } from '../Presets';
import { limitDateRange } from '../range';
import type {
  DateRange,
  DateRangePickerInteraction,
  LabeledPreset,
} from '../types';
import { type DateTimePrecision, getPrecision } from './inputFormat';

interface PickerContextValue {
  activePreset?: LabeledPreset;
  apply: (value?: DateRange) => void;
  cancel: () => void;
  close: () => void;
  draftValue: DateRange;
  emitInteraction: (interaction: DateRangePickerInteraction) => void;
  isOpen: boolean;
  maxDurationSeconds?: number;
  open: () => void;
  presets: readonly LabeledPreset[];
  precision: DateTimePrecision;
  setDraftValue: (value: DateRange) => void;
  timeZone?: TimeZone;
  value: DateRange;
}

const PickerContext = createContext<PickerContextValue | undefined>(undefined);

export interface PickerProviderProps {
  children: ReactNode;
  maxDurationSeconds?: number;
  onChange: (value: DateRange) => void;
  onInteraction?: (interaction: DateRangePickerInteraction) => void;
  presets?: readonly LabeledPreset[];
  timeZone?: TimeZone;
  value: DateRange;
}

export function PickerProvider({
  children,
  maxDurationSeconds,
  onChange,
  onInteraction,
  presets = [],
  timeZone,
  value,
}: PickerProviderProps) {
  const displayValue = limitDateRange(value, value, maxDurationSeconds);
  const [isOpen, setIsOpen] = useState(false);
  const [draftValue, setDraftValueState] = useState(displayValue);
  const precision = getPrecision(draftValue);
  const activePreset = getActivePreset(draftValue, { presets, timeZone });

  const limitValue = (nextValue: DateRange, previousValue: DateRange) =>
    limitDateRange(previousValue, nextValue, maxDurationSeconds);

  const open = () => {
    setDraftValueState(limitDateRange(value, value, maxDurationSeconds));
    setIsOpen(true);
  };

  const close = () => {
    setIsOpen(false);
  };

  const emitInteraction = (interaction: DateRangePickerInteraction) => {
    onInteraction?.(interaction);
  };

  const cancel = () => {
    setIsOpen(false);
  };

  const setDraftValue = (nextValue: DateRange) => {
    setDraftValueState((previousValue) => limitValue(nextValue, previousValue));
  };

  const apply = (nextValue: DateRange = draftValue) => {
    onChange(limitValue(nextValue, draftValue));
    setIsOpen(false);
  };

  return (
    <PickerContext.Provider
      value={{
        activePreset,
        apply,
        cancel,
        close,
        draftValue,
        emitInteraction,
        isOpen,
        maxDurationSeconds,
        open,
        presets,
        precision,
        setDraftValue,
        timeZone,
        value: displayValue,
      }}
    >
      {children}
    </PickerContext.Provider>
  );
}

export function usePicker() {
  const context = useContext(PickerContext);

  if (context === undefined) {
    throw new Error('usePicker must be used within PickerProvider');
  }

  return context;
}
